import { create } from "zustand";
import type {
  User,
  Therapist,
  AuthSession,
  AuthCredentials,
} from "@msb/shared";
import { subscribeToAuthState, getGlobalAuthState } from "./authContextBridge";
import { useEnhancedAuthStore, cleanupEnhancedAuth } from "./enhancedAuth";

interface AuthState {
  session: AuthSession | null;
  currentUser: User | null;
  currentTherapist: Therapist | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Actions
  login: (credentials: AuthCredentials) => Promise<boolean>;
  logout: () => void;
  clearError: () => void;
  getCurrentTherapist: () => Therapist | null;
  syncWithEnhancedStore: () => void;

  // New Firebase methods
  loginWithFirebase: (email: string, password: string) => Promise<boolean>;
  registerWithFirebase: (email: string, password: string, displayName?: string, profile?: { prefix?: string; firstName: string; lastName: string }) => Promise<boolean>;
  loginWithGoogle: () => Promise<boolean>;
  getToken: () => Promise<string | null>;
  initialize: () => Promise<void>;
  switchToFirebase: () => Promise<void>;
  switchToJWT: () => void;
}

// Create a wrapper store that uses the enhanced auth store internally
// Note: persist() middleware removed - Firebase Auth handles persistence automatically
export const useAuthStore = create<AuthState>()((_set, _get) => ({
  // Initialize with enhanced store values
  session: null,
      currentUser: null,
      currentTherapist: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,
      error: null,

      // Helper function to sync with enhanced store
      syncWithEnhancedStore: () => {
        const enhancedStore = useEnhancedAuthStore.getState();
        _set({
          session: enhancedStore.session,
          currentUser: enhancedStore.currentUser,
          currentTherapist: enhancedStore.currentTherapist,
          isAuthenticated: enhancedStore.isAuthenticated,
          isLoading: enhancedStore.isLoading,
          isInitialized: enhancedStore.isInitialized,
          error: enhancedStore.error,
        });
      },

      // Delegate to enhanced store
      login: async (credentials: AuthCredentials): Promise<boolean> => {
        const enhancedStore = useEnhancedAuthStore.getState();
        const result = await enhancedStore.login(credentials, 'jwt');
        _get().syncWithEnhancedStore();
        return result;
      },

      loginWithFirebase: async (email: string, password: string): Promise<boolean> => {
        const enhancedStore = useEnhancedAuthStore.getState();
        const result = await enhancedStore.loginWithFirebase(email, password);
        _get().syncWithEnhancedStore();
        return result;
      },

      registerWithFirebase: async (email: string, password: string, displayName?: string, profile?: { prefix?: string; firstName: string; lastName: string }): Promise<boolean> => {
        const enhancedStore = useEnhancedAuthStore.getState();
        const result = await enhancedStore.registerWithFirebase(email, password, displayName, profile);
        _get().syncWithEnhancedStore();
        return result;
      },

      loginWithGoogle: async (): Promise<boolean> => {
        const enhancedStore = useEnhancedAuthStore.getState();
        const result = await enhancedStore.loginWithGoogle();
        _get().syncWithEnhancedStore();
        return result;
      },


      logout: async (): Promise<void> => {
        const enhancedStore = useEnhancedAuthStore.getState();
        await enhancedStore.logout();
        _get().syncWithEnhancedStore();
      },

      clearError: () => {
        const enhancedStore = useEnhancedAuthStore.getState();
        enhancedStore.clearError();
        _get().syncWithEnhancedStore();
      },

      getCurrentTherapist: (): Therapist | null => {
        const enhancedStore = useEnhancedAuthStore.getState();
        return enhancedStore.getCurrentTherapist();
      },

      getToken: async (): Promise<string | null> => {
        const enhancedStore = useEnhancedAuthStore.getState();
        return await enhancedStore.getToken();
      },

      initialize: async (): Promise<void> => {
        const enhancedStore = useEnhancedAuthStore.getState();
        await enhancedStore.initialize();
        _get().syncWithEnhancedStore();
      },

      switchToFirebase: async (): Promise<void> => {
        const enhancedStore = useEnhancedAuthStore.getState();
        await enhancedStore.switchToFirebase();
        _get().syncWithEnhancedStore();
      },

      switchToJWT: (): void => {
        const enhancedStore = useEnhancedAuthStore.getState();
        enhancedStore.switchToJWT();
        _get().syncWithEnhancedStore();
      },
    }));

// Subscribe to enhanced store changes and sync with main store
useEnhancedAuthStore.subscribe((state) => {
  useAuthStore.setState({
    session: state.session,
    currentUser: state.currentUser,
    currentTherapist: state.currentTherapist,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    isInitialized: state.isInitialized,
    error: state.error,
  });
});

// Subscribe to global auth state from AuthContext (primary source of truth)
subscribeToAuthState((globalState) => {
  useAuthStore.setState({
    currentUser: globalState.currentUser,
    currentTherapist: globalState.currentTherapist,
    isAuthenticated: globalState.isAuthenticated,
    isLoading: globalState.loading,
    isInitialized: !globalState.loading, // initialized when loading is false
    error: globalState.error,
    session: globalState.currentUser ? {
      user: globalState.currentUser,
      token: '', // Token is managed by Firebase
      expiresAt: new Date(Date.now() + 3600000), // 1 hour
    } : null,
  });
  
  // Also sync with enhanced store for backward compatibility
  useEnhancedAuthStore.setState({
    currentUser: globalState.currentUser,
    currentTherapist: globalState.currentTherapist,
    firebaseUser: globalState.firebaseUser,
    isAuthenticated: globalState.isAuthenticated,
    isLoading: globalState.loading,
    isInitialized: !globalState.loading,
    error: globalState.error,
  });
});

// Initialize with current global state
const initialState = getGlobalAuthState();
useAuthStore.setState({
  currentUser: initialState.currentUser,
  currentTherapist: initialState.currentTherapist,
  isAuthenticated: initialState.isAuthenticated,
  isLoading: initialState.loading,
  isInitialized: !initialState.loading,
  error: initialState.error,
});

// Cleanup on app unmount
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    cleanupEnhancedAuth();
  });
}
